import { DEFAULT_PAGE_INFO } from '@apeiron/common'
import {
  ConstellationTypeID,
  GalaxyID,
  StarLite,
  StarSortBy
} from '@apeiron/star'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { starLiteSchema } from '@src/deserialize/yup/star'
import graphql from '@src/graphql/query/getMyStars'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetMyStars = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<StarLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const {
        bookmarked = false,
        filterInput = {},
        pageInput = {},
        sortInput = { sortBy: StarSortBy.StarIdAsc }
      } = request

      const response = await query({
        variables: {
          bookmarked,
          filterInput: R.reject(R.isNil, filterInput),
          pagingInput: serializePagingInput(pageInput),
          sortInput
        }
      })

      const stars = deserializeListData<StarLite>(response, starLiteSchema)

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(stars)

      setPageInfo(pageInfo)

      return {
        data: stars,
        pageInfo
      }
    },
    [query]
  )

  return {
    data,
    loading,
    pageInfo,
    fetch
  }
}

type Request = {
  bookmarked?: boolean
  filterInput: {
    tier?: number
    galaxy?: GalaxyID
    constellationType?: ConstellationTypeID
    constellation?: number
  }
  pageInput?: PageInput
  sortInput?: StarSortBy
}

type Response = {
  data: StarLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: StarLite[]
  fetch: (request: Request) => Promise<Response>
  loading: boolean
  pageInfo: PageInfo
}

export default useGetMyStars
